/**
 * @fileoverview Labs API client module
 */

import apiClient from './client'

/**
 * @typedef {Object} LabListParams
 * @property {string} [status] - Filter by status (provisioning, running, stopped, error, destroying)
 * @property {number} [page] - Page number for pagination
 * @property {number} [limit] - Items per page
 */

/**
 * @typedef {Object} LabListItem
 * @property {string} id - Lab ID
 * @property {string} name - Lab name
 * @property {string} template_slug - Template slug
 * @property {string} template_name - Template name
 * @property {string} status - Lab status
 * @property {string} region - AWS region code where lab is deployed
 * @property {string} created_at - ISO timestamp of creation
 * @property {string} [expires_at] - ISO timestamp of expiration
 * @property {number} vm_count - Number of VMs
 * @property {number} estimated_cost_per_hour - Hourly cost estimate
 * @property {Object} [capabilities] - RBAC capability flags for the current user
 * @property {boolean} [capabilities.read] - Can view lab data
 * @property {boolean} [capabilities.update] - Can operate the lab
 * @property {boolean} [capabilities.create] - Can create subordinate resources/shares
 * @property {boolean} [capabilities.delete] - Can destroy or revoke
 * @property {boolean} [can_manage_external_shares] - Whether the current user may manage external shares
 * @property {'viewer'|'editor'|'admin'|'owner'|null} [current_user_role] - Effective RBAC role
 */

/**
 * @typedef {Object} PaginatedLabsResponse
 * @property {LabListItem[]} items - Labs in current page
 * @property {number} total - Total number of labs
 * @property {number} page - Current page number (1-indexed)
 * @property {number} limit - Items per page
 */

/**
 * @typedef {Object} CreateLabRequest
 * @property {string} template_slug - Template slug to create lab from
 * @property {string} name - Human-readable lab name
 * @property {number} [expires_hours=4] - Hours until lab expires (default: 4)
 * @property {string} [region='us-east-1'] - AWS region for lab deployment
 * @property {Object<string, string|number|boolean>} [variables] - User-provided variable values keyed by variable name
 */

/**
 * @typedef {Object} LabDetail
 * @property {string} id - Lab ID (prefixed, e.g., lab-xxx)
 * @property {string} name - Lab name
 * @property {string} template_slug - Template slug
 * @property {string} template_name - Template display name
 * @property {string} status - Lab status (provisioning, running, stopped, error, destroying, destroyed)
 * @property {string} [status_message] - Status details or error message
 * @property {string} region - AWS region code where lab is deployed (e.g., us-east-1)
 * @property {string} created_at - ISO timestamp of creation
 * @property {string} [started_at] - ISO timestamp when lab started running
 * @property {string} [expires_at] - ISO timestamp of expiration
 * @property {number} estimated_cost_per_hour - Hourly cost estimate in USD
 * @property {Object} [terraform_outputs] - Terraform output values
 * @property {Object} owner - Canonical lab owner
 * @property {string} owner.id - Owner user ID
 * @property {string} owner.email - Owner email address
 * @property {string} owner.display_name - Owner display name
 * @property {Object} capabilities - RBAC capability flags for the current user
 * @property {boolean} capabilities.read - Can view lab data
 * @property {boolean} capabilities.update - Can operate the lab
 * @property {boolean} capabilities.create - Can create subordinate resources/shares
 * @property {boolean} capabilities.delete - Can destroy or revoke
 * @property {boolean} can_manage_external_shares - Whether the current user may manage external shares
 * @property {'viewer'|'editor'|'admin'|'owner'|null} current_user_role - Effective RBAC role
 * @property {Object<string, string|number|boolean>} [variables] - Lab variable values (sensitive values masked)
 */

/**
 * @typedef {Object} ExtendLabRequest
 * @property {number} hours - Hours to extend expiration
 */

/**
 * @typedef {Object} VMListItem
 * @property {string} id - VM ID
 * @property {string} name - VM name
 * @property {string} os - Operating system
 * @property {string} status - VM status (running, stopped, error)
 * @property {string} [ip_address] - VM IP address
 * @property {number} cpu - CPU count
 * @property {number} memory_gb - Memory in GB
 */

/**
 * @typedef {Object} VMDetail
 * @property {string} id - VM ID
 * @property {string} name - VM name
 * @property {string} os - Operating system
 * @property {string} status - VM status
 * @property {string} [ip_address] - VM IP address
 * @property {number} cpu - CPU count
 * @property {number} memory_gb - Memory in GB
 * @property {number} disk_gb - Disk size in GB
 * @property {Object} [metadata] - Additional metadata
 */

/**
 * @typedef {Object} ConsoleCredentials
 * @property {string} websocket_url - WebSocket URL with embedded encrypted token
 * @property {string} connection_id - VM connection identifier
 */

/**
 * @typedef {Object} LabVariableValue
 * @property {string} name - Variable name (key)
 * @property {string} label - Display label
 * @property {string|number|boolean|null} value - Actual value or "********" if sensitive
 * @property {boolean} sensitive - Whether the value is masked
 */

/**
 * @typedef {Object} LabVariableValidationError
 * @property {string} field - Variable name that failed validation
 * @property {string} message - Error message describing the validation failure
 */

/**
 * @typedef {Object} LabCreateError
 * @property {string} message - General error message
 * @property {Object} [details] - Detailed error information
 * @property {Object<string, string>} [details.variables] - Variable-specific validation errors (variable name -> error message)
 */

/**
 * @typedef {Object} PowerControlRequest
 * @property {'start'|'stop'|'restart'} action - Power action to perform
 */

/**
 * Get list of labs for current user
 *
 * @param {LabListParams} [params={}] - Query parameters
 * @returns {Promise<PaginatedLabsResponse>} Paginated labs response
 * @example
 * const { items, total, page, limit } = await getLabs({ status: 'running', page: 1, limit: 10 })
 */
export async function getLabs(params = {}) {
  const response = await apiClient.get('/v1/labs', { params })
  return response.data
}

/**
 * Get detailed information about a specific lab
 *
 * @param {string} id - Lab ID
 * @returns {Promise<LabDetail>} Lab details including masked variable values
 * @example
 * const lab = await getLab('lab-123')
 *
 * // Access lab variables (sensitive values are masked)
 * if (lab.variables) {
 *   Object.entries(lab.variables).forEach(([key, value]) => {
 *     console.debug(key, value) // e.g., 'admin_password', '********'
 *   })
 * }
 */
export async function getLab(id) {
  const response = await apiClient.get(`/v1/labs/${id}`)
  return response.data
}

/**
 * Create a new lab from a template
 *
 * @param {CreateLabRequest} data - Lab creation data
 * @returns {Promise<LabDetail>} Created lab details
 * @throws {Error} Throws error with details.variables for validation failures
 * @example
 * // Basic lab creation (uses default region us-east-1)
 * const lab = await createLab({
 *   template_slug: 'kubernetes-basics',
 *   name: 'My K8s Lab',
 *   expires_hours: 8
 * })
 *
 * // Lab in specific region with variables
 * const lab = await createLab({
 *   template_slug: 'windows-ad-lab',
 *   name: 'AD Training Lab',
 *   expires_hours: 24,
 *   region: 'eu-west-1',  // Deploy in Ireland for GDPR compliance
 *   variables: {
 *     admin_password: 'SecureP@ss123',
 *     domain_name: 'training.local',
 *     user_count: 10
 *   }
 * })
 *
 * // Handle variable validation errors
 * try {
 *   await createLab(data)
 * } catch (error) {
 *   if (error.response?.data?.details?.variables) {
 *     // error.response.data.details.variables = { admin_password: 'Must be at least 12 characters' }
 *     console.error('Variable errors:', error.response.data.details.variables)
 *   }
 * }
 */
export async function createLab(data) {
  const response = await apiClient.post('/v1/labs', data)
  return response.data
}

/**
 * Delete and destroy a lab
 *
 * @param {string} id - Lab ID
 * @returns {Promise<{message: string}>} Deletion confirmation
 * @example
 * await deleteLab('lab-123')
 */
export async function deleteLab(id) {
  const response = await apiClient.delete(`/v1/labs/${id}`)
  return response.data
}

/**
 * Start a stopped lab
 *
 * @param {string} id - Lab ID
 * @returns {Promise<LabDetail>} Updated lab details
 * @example
 * const lab = await startLab('lab-123')
 */
export async function startLab(id) {
  const response = await apiClient.post(`/v1/labs/${id}/start`)
  return response.data
}

/**
 * Stop a running lab
 *
 * @param {string} id - Lab ID
 * @returns {Promise<LabDetail>} Updated lab details
 * @example
 * const lab = await stopLab('lab-123')
 */
export async function stopLab(id) {
  const response = await apiClient.post(`/v1/labs/${id}/stop`)
  return response.data
}

/**
 * Extend lab expiration time
 *
 * @param {string} id - Lab ID
 * @param {ExtendLabRequest} data - Extension data
 * @returns {Promise<LabDetail>} Updated lab details
 * @example
 * const lab = await extendLab('lab-123', { hours: 4 })
 */
export async function extendLab(id, data) {
  const response = await apiClient.post(`/v1/labs/${id}/extend`, data)
  return response.data
}

/**
 * Get list of VMs in a lab
 *
 * @param {string} id - Lab ID
 * @returns {Promise<VMListItem[]>} List of VMs
 * @example
 * const vms = await getLabVMs('lab-123')
 */
export async function getLabVMs(id) {
  const response = await apiClient.get(`/v1/labs/${id}/vms`)
  return response.data
}

/**
 * Get detailed information about a specific VM
 *
 * @param {string} labId - Lab ID
 * @param {string} vmId - VM ID
 * @returns {Promise<VMDetail>} VM details
 * @example
 * const vm = await getVM('lab-123', 'vm-456')
 */
export async function getVM(labId, vmId) {
  const response = await apiClient.get(`/v1/labs/${labId}/vms/${vmId}`)
  return response.data
}

/**
 * Get console connection for a VM (credential-free)
 *
 * Returns WebSocket connection information with encrypted token embedded in URL.
 * The backend retrieves encrypted credentials from database and generates a
 * secure WebSocket URL with embedded authentication token.
 *
 * SECURITY: Frontend NEVER sees VM credentials - they remain encrypted in database
 * and are only decrypted server-side when establishing console connections.
 *
 * @param {string} labId - Lab ID
 * @param {string} vmId - VM ID
 * @returns {Promise<ConsoleCredentials>} WebSocket connection details
 * @example
 * const { websocket_url, connection_id } = await getVMConsole('lab-123', 'vm-456')
 * // websocket_url format: wss://console.autolabs.com/ws/{encrypted_token}
 * // Token contains: labId, vmId, protocol, decrypted credentials, expiry
 * connect(websocket_url)
 */
export async function getVMConsole(labId, vmId) {
  const response = await apiClient.get(`/v1/labs/${labId}/vms/${vmId}/console`)
  return response.data
}

/**
 * Control VM power state (start/stop/restart)
 *
 * @param {string} labId - Lab ID
 * @param {string} vmId - VM ID
 * @param {'start'|'stop'|'restart'} action - Power action to perform
 * @returns {Promise<VMDetail>} Updated VM details
 * @example
 * const vm = await controlVMPower('lab-123', 'vm-456', 'restart')
 */
export async function controlVMPower(labId, vmId, action) {
  const response = await apiClient.post(`/v1/labs/${labId}/vms/${vmId}/power`, { action })
  return response.data
}

/**
 * @typedef {Object} VMHealthStatus
 * @property {string} vm_id - VM ID
 * @property {string} ec2_state - EC2 instance state (running, stopped, pending, etc.)
 * @property {string|null} system_status - EC2 system status (ok, impaired, initializing, null)
 * @property {string|null} instance_status - EC2 instance status (ok, impaired, initializing, null)
 * @property {boolean} status_changed - Whether the VM status changed during refresh
 */

/**
 * @typedef {Object} HealthCheckResult
 * @property {string} lab_id - Lab ID
 * @property {string} checked_at - ISO timestamp when health check was performed
 * @property {VMHealthStatus[]} vm_statuses - Health status for each VM
 * @property {string[]} issues - List of health issues found
 * @property {boolean} all_healthy - Whether all VMs are healthy
 */

/**
 * Refresh VM health status for a lab
 *
 * Triggers an on-demand health check that queries EC2 API for fresh VM status
 * and system/instance health information. Updates VM statuses in database.
 *
 * @param {string} labId - Lab ID
 * @returns {Promise<HealthCheckResult>} Health check results with VM statuses and issues
 * @example
 * const result = await refreshLabStatus('lab-123')
 * console.debug('Last checked:', result.checked_at)
 * console.debug('All healthy:', result.all_healthy)
 * console.debug('Issues:', result.issues)
 * result.vm_statuses.forEach(vm => {
 *   console.debug(`${vm.vm_id}: ${vm.ec2_state} (system: ${vm.system_status}, instance: ${vm.instance_status})`)
 * })
 */
export async function refreshLabStatus(labId) {
  const response = await apiClient.post(`/v1/labs/${labId}/refresh-status`)
  return response.data
}

/**
 * Reprovision a lab in error status using the latest template
 *
 * @param {string} id - Lab ID
 * @returns {Promise<{message: string}>} Reprovision confirmation
 * @example
 * await reprovisionLab('lab-123')
 */
export async function reprovisionLab(id) {
  const response = await apiClient.post(`/v1/labs/${id}/reprovision`)
  return response.data
}
